import React, { useEffect, useState } from 'react';
import { breakpoints, layout, Tab, Tabs } from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { trackAnalyticsEvent } from 'analytics';
import { camelCase, head } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import { horizontalAppRoutes } from 'App/appRoutes';
import { appsPagePath } from 'App/Apps';
import { useUserAppsPath } from 'App/Navigation/useUserAppsPath';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { LeftNavRoute } from 'Router/ReactRoute';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { GeneralUserEventPayload } from 'Workspace/TelemetryEvents/generalEvent/GeneralUserEventPayload';
import { useUserContext } from 'Workspace/UserContext';
import { isHorizontalTabRoute } from './checkHorizontalTabRoute';

const StyledTabsLegacy = styled(Tabs)`
	padding: ${layout.space32} ${layout.space64} 0 ${layout.space64};
	background-color: inherit;
`;

const StyledTabsDetailed = styled(StyledTabsLegacy)`
	margin: 0 auto;
	max-width: ${breakpoints.centerContentWidthPX}px;
	padding: ${layout.space32} ${layout.space20} 0 ${layout.space20};
`;

export const Navigation = () => {
	const location = useLocation();
	const { workspaceConfiguration } = useConfigurationContext();
	const history = useHistory();
	const userContext = useUserContext();
	const resources = useLoadableResourceContext();
	const [activeTab, setActiveTab] = useState('');
	const userAppsPath = useUserAppsPath();
	const isCenterAlignClassicViewEnabled = useFeatureCanary(
		FeatureFlag.CenterAlignClassicView
	);
	const changeActiveTab = (tab: string) => {
		let path = tab;
		trackAnalyticsEvent(
			GeneralUserEventPayload.screenSwitch(camelCase(activeTab), camelCase(tab))
		);
		if (tab === appsPagePath) {
			path = userAppsPath;
		}
		history.push(path);
		setActiveTab(tab);
	};

	useEffect(() => {
		setActiveTab(`/${head(history.location.pathname.slice(1).split('/'))}`);
	}, [history.location]);

	function isRouteAvailable(route: LeftNavRoute): boolean {
		return (
			route.isAvailable({
				location,
				workspaceConfiguration,
				userContext,
				resourceContext: resources.value,
			}) && !!route.component
		);
	}

	function transformRouteToTab(route: LeftNavRoute) {
		const navigationLink = route.getNavLinkProps(workspaceConfiguration);
		return (
			<Tab
				label={navigationLink.title}
				value={head(route.paths)}
				key={navigationLink.title}
				id={navigationLink.analyticsID}
			/>
		);
	}

	const availableAppRoutes = horizontalAppRoutes.filter(route => isRouteAvailable(route));

	const navigationNeeded =
		availableAppRoutes.length > 1 && isHorizontalTabRoute(location);

	const StyledTabs = isCenterAlignClassicViewEnabled
		? StyledTabsDetailed
		: StyledTabsLegacy;

	return (
		navigationNeeded && (
			<StyledTabs activeTab={activeTab} onChange={changeActiveTab}>
				{availableAppRoutes.map(route => transformRouteToTab(route))}
			</StyledTabs>
		)
	);
};
